import React, { useState } from "react";
import { Link } from "react-router-dom";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { HelpCircle, ArrowRight } from "lucide-react";

/**
 * P7 — Score explanation modal.
 * Used on Dashboard, SampleReport, and Demo pages. Same hard-coded copy
 * everywhere so users get a consistent informational explanation.
 */
export default function ScoreExplanationModal() {
  const [open, setOpen] = useState(false);
  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="inline-flex items-center gap-1 text-xs text-[var(--rg-text)] hover:text-[var(--rg-sage)] mt-2"
        data-testid="score-explain-trigger"
      >
        <HelpCircle className="w-3.5 h-3.5" /> What does this score mean?
        <ArrowRight className="w-3 h-3" />
      </button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent
          className="bg-[var(--rg-surface)] border-[var(--rg-border)] text-[var(--rg-text-strong)] max-w-2xl"
          data-testid="score-explain-modal"
        >
          <DialogHeader>
            <DialogTitle className="font-heading">Understanding Your Readiness Score</DialogTitle>
            <DialogDescription className="text-[var(--rg-text)] text-sm">
              How the Regulens 0–100 score is calculated and what each band means.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 text-sm text-[var(--rg-text)] leading-relaxed">
            <p>
              Your Readiness Score is a 0&ndash;100 informational indicator of how well your
              store profile aligns with publicly available regulatory reference points across
              your selected jurisdictions.
            </p>
            <div className="rounded-md border border-[var(--rg-amber)] bg-[var(--rg-amber-soft)] p-3 text-xs text-[var(--rg-amber)] leading-relaxed">
              It is <strong>NOT</strong> a legal compliance certificate. It is <strong>NOT</strong> a
              guarantee of compliance. It is an informational snapshot to help you identify where
              to focus attention.
            </div>

            <div>
              <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-[var(--rg-sage)] mb-2">
                Score bands
              </div>
              <ul className="space-y-1.5 text-sm">
                <li><span className="font-mono text-[var(--rg-sage)] font-bold">A (90&ndash;100)</span>: Strong Readiness &mdash; your profile aligns well with regulatory reference points across selected states.</li>
                <li><span className="font-mono text-[var(--rg-sage)] font-bold">B (75&ndash;89)</span>: Good Readiness &mdash; minor gaps identified. Review recommendations.</li>
                <li><span className="font-mono text-[var(--rg-amber)] font-bold">C (60&ndash;74)</span>: Moderate Readiness &mdash; several gaps identified. Advisor consultation suggested.</li>
                <li><span className="font-mono text-[var(--rg-amber)] font-bold">D (40&ndash;59)</span>: Low Readiness &mdash; significant gaps. Professional review recommended.</li>
                <li><span className="font-mono text-[var(--rg-red)] font-bold">F (0&ndash;39)</span>: Significant Gaps &mdash; immediate professional review strongly recommended.</li>
              </ul>
            </div>

            <div>
              <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-[var(--rg-sage)] mb-2">
                Calculation
              </div>
              <p>Your score is calculated from 4 pillars:</p>
              <ul className="list-disc list-inside text-sm mt-1.5 space-y-0.5">
                <li>Sales Tax Nexus <span className="text-[var(--rg-muted)] font-mono">(35%)</span></li>
                <li>Privacy Law Readiness <span className="text-[var(--rg-muted)] font-mono">(30%)</span></li>
                <li>Policy Documents <span className="text-[var(--rg-muted)] font-mono">(20%)</span></li>
                <li>Exposure Awareness <span className="text-[var(--rg-muted)] font-mono">(15%)</span></li>
              </ul>
            </div>

            <div className="pt-2">
              <Link
                to="/methodology"
                className="text-[var(--rg-sage)] hover:underline inline-flex items-center gap-1"
                data-testid="score-explain-methodology"
                onClick={() => setOpen(false)}
              >
                View full methodology <ArrowRight className="w-3.5 h-3.5" />
              </Link>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
